import React from "react";
import { Link } from "react-router-dom";
import InsideEveryBar from "./InsideEveryBar";
import { useScrollToHash } from "../lib/useScrollToHash";

const IMG_RHUBARB = "https://customer-assets.emergentagent.com/job_soap-made-proper/artifacts/qc8xdqm5_Infused%20with%20Himalayan%20Rhubarb%20Cinnamon%20and%20clove%20Tallow%20soap.jpg";
const IMG_CACAO = "https://customer-assets.emergentagent.com/job_soap-made-proper/artifacts/2mrz3li6_Infused%20with%20Cacao%20Powder%20organic%20Tallow%20soap.jpg";
const IMG_MADDER = "https://customer-assets.emergentagent.com/job_soap-made-proper/artifacts/moho1bn4_Infused%20with%20Madder%20Root%20Cinnamon%20Activated%20charcoal%20tallow%20soap.jpg";
const IMG_SALT = "https://customer-assets.emergentagent.com/job_soap-made-proper/artifacts/u7skbuj9_Alati%20signature%20salt%20bar%20tallow%20soap%20infused%20with%20Indigo%20powder.jpg";
const IMG_INDIGO = "https://customer-assets.emergentagent.com/job_soap-made-proper/artifacts/m17wbfho_Bannana%20Activated%20charcoal%20Indigo%20powder%20Tallow%20Soap.jpg";

const TheProcess = () => {
    useScrollToHash();
    return (
        <div data-testid="the-process-page" className="bg-[#FAF9F6]">
            <section className="py-20 lg:py-28 px-6 lg:px-10">
                <div className="max-w-4xl mx-auto">
                    <p className="label">From Plant to Bar</p>
                    <h1 className="font-serif text-5xl sm:text-6xl mt-5 leading-[1.04]">
                        We start with the oil and move outward.
                    </h1>
                    <p className="mt-8 max-w-2xl text-lg text-[#4A4A46] leading-relaxed">
                        Before many of our soaps are ever poured into a mould, the oils begin
                        another journey. Weeks before a bar is made, carefully chosen
                        botanicals are slowly infused into olive oil or other selected oils.
                    </p>
                </div>
            </section>

            <section className="px-6 lg:px-10 pb-20">
                <div className="max-w-5xl mx-auto product-image-wrap aspect-[16/9]">
                    <img
                        src={IMG_RHUBARB}
                        alt="Slow Himalayan rhubarb oil infusion"
                        className="w-full h-full object-cover"
                    />
                </div>
            </section>

            <section className="px-6 lg:px-10 pb-20">
                <div className="max-w-2xl mx-auto">
                    <div className="space-y-6 text-lg leading-relaxed text-[#4A4A46]">
                        <p>
                            Some bring natural colour. Some bring subtle botanical character.
                            Some continue traditions that have been part of our family for
                            generations.
                        </p>
                        <p>
                            Nothing is rushed. Nature is given time to do what it has always
                            done. That patience becomes part of every bar.
                        </p>
                    </div>
                </div>
            </section>

            <section className="bg-[#F0EBE1] py-20 px-6 lg:px-10">
                <div className="max-w-3xl mx-auto text-center">
                    <p className="label">The ALATI Slow Infusion Method</p>
                    <h2 className="font-serif text-3xl sm:text-4xl mt-5 leading-snug text-[#1C1C1A]">
                        Nothing is added just for show.
                        <br />
                        Everything has a reason.
                    </h2>
                    <p className="mt-8 text-[#4A4A46] leading-relaxed max-w-2xl mx-auto">
                        We do not add dried flowers simply because they look beautiful. We do
                        not use synthetic colours because they are brighter. Every botanical
                        has a reason. Every oil is chosen carefully. Every recipe begins with
                        intention.
                    </p>
                </div>
            </section>

            {/* Steps */}
            {[
                {
                    n: "01",
                    title: "Slow oil infusion",
                    body: "Weeks before a single bar is made, olive oil is slowly infused with the chosen botanicals — calendula, Himalayan rhubarb, madder root, turmeric, indigo, basil, sage, rosemary, Greek mountain herbs, depending on the bar. The oil takes on colour, character and quiet scent from the plants alone.",
                    image: IMG_MADDER,
                },
                {
                    n: "02",
                    title: "Traditional saponification",
                    body: "Oils and grass-fed tallow are combined and saponified using either cold process or hot process methods. Both are traditional. Both are slow. The glycerin produced during saponification is retained naturally in every bar.",
                    image: IMG_CACAO,
                },
                {
                    n: "03",
                    title: "Hand-poured, in small batches",
                    body: "Every bar is poured into wooden moulds, the surface swirled by hand. Each batch is small. Each batch is its own quiet record.",
                    image: IMG_SALT,
                },
                {
                    n: "04",
                    title: "Cured for weeks",
                    body: "Bars cure slowly on open racks for four to six weeks. Castile soaps take far longer. Some things should never be rushed.",
                    image: IMG_RHUBARB,
                },
                {
                    n: "05",
                    title: "Cut, stamped, wrapped",
                    body: "We cut each loaf by hand and wrap each bar in simple, unbleached paper. Every bar varies slightly. That is the mark of real botanical colour.",
                    image: IMG_INDIGO,
                },
            ].map((s, i) => (
                <section key={s.n} className="px-6 lg:px-10 py-16 lg:py-24" data-testid={`process-step-${s.n}`}>
                    <div className={`max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-20 items-center`}>
                        <div className={`lg:col-span-6 ${i % 2 === 1 ? "lg:order-2" : ""}`}>
                            <div className="product-image-wrap aspect-[4/5]">
                                <img src={s.image} alt={s.title} loading="lazy" className="w-full h-full object-cover" />
                            </div>
                        </div>
                        <div className={`lg:col-span-6 ${i % 2 === 1 ? "lg:order-1" : ""}`}>
                            <p className="font-serif text-7xl text-[#C7C0B3]">{s.n}</p>
                            <h2 className="font-serif text-4xl mt-4 leading-tight">{s.title}</h2>
                            <p className="mt-6 text-[#4A4A46] text-lg leading-relaxed">{s.body}</p>
                        </div>
                    </div>
                </section>
            ))}

            <section className="bg-[#F0EBE1] py-20 px-6 lg:px-10">
                <div className="max-w-3xl mx-auto text-center">
                    <p className="label">Honest soap</p>
                    <h2 className="font-serif text-3xl sm:text-4xl mt-5 leading-snug">
                        No synthetic fragrance.
                        <br />
                        No synthetic colourants.
                        <br />
                        Just simple, beautiful ingredients.
                    </h2>
                    <div className="mt-10">
                        <a href="#ingredients" className="btn-secondary" data-testid="process-ingredients-cta">
                            Inside Every Bar
                        </a>
                    </div>
                </div>
            </section>

            {/* INGREDIENTS section — merged from former /ingredients page */}
            <div id="ingredients" data-testid="ingredients-anchor">
                <InsideEveryBar />
            </div>
        </div>
    );
};

export default TheProcess;
